import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Divider,
  RadioGroup,
  Radio,
  Icon,
  IconButton,
  Spinner,
  HStack,
  Stack,
  useToast,
  useDisclosure,
  SimpleGrid,
  FormErrorMessage,
  VStack,
  Badge,
  Card,
  CardBody
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaApple, FaBitcoin, FaCreditCard } from 'react-icons/fa';
import { Controller, useForm } from 'react-hook-form';
import { useSubscription } from '../../../context/SubscriptionContext';
import { useAuth } from '../../../hooks/useAuth';
import ConfirmationModal from '../../../tools/ConfirmationModal';
import CustomSelect from '../../../utils/CustomSelect';

const PaymentMethodSelect = ({ selectedPaymentMethod, setSelectedPaymentMethod }) => (
  <FormControl w="100%" mt={4} bg="secondary.900" p={3} borderRadius="md">
    <FormLabel fontSize="lg" fontWeight="bold" color="primary.500">
      Payment Method
    </FormLabel>
    <RadioGroup onChange={setSelectedPaymentMethod} value={selectedPaymentMethod}>
      <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="card" colorScheme="blue">
            <HStack>
              <Icon as={FaCreditCard} boxSize={5} />
              <Text color="primary.500">Card</Text>
            </HStack>
          </Radio>
        </Box>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="applePay" colorScheme="blue" isDisabled>
            <HStack>
              <Icon as={FaApple} boxSize={5} />
              <Text color="primary.500">Apple Pay</Text>
            </HStack>
          </Radio>
        </Box>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="cryptoPay" colorScheme="blue" isDisabled>
            <HStack>
              <Icon as={FaBitcoin} boxSize={5} />
              <Text color="primary.500">Crypto Pay</Text>
            </HStack>
          </Radio>
        </Box>
      </Stack>
    </RadioGroup>
  </FormControl>
);

const BillingDetailsForm = ({ register, errors, countries, control }) => {
  return (
    <Box color="primary.100">
      <FormControl fontSize={{ base: 'sm', md: 'md' }} mt={4} color="primary.100" isInvalid={errors.cardName}>
        <FormLabel>Name on Card</FormLabel>
        <Input
          type="text"
          {...register("cardName", { required: "Name on card is required" })}
          variant="filled"
        />
        <FormErrorMessage>{errors.cardName && errors.cardName.message}</FormErrorMessage>
      </FormControl>
      <SimpleGrid columns={2} spacing={4} mt={4}>
        <FormControl fontSize={{ base: 'sm', md: 'md' }} color="primary.100" isInvalid={errors.billingAddress}>
          <FormLabel fontSize={{ base: 'sm', md: 'md' }} color="primary.100">
            Billing Address
          </FormLabel>
          <Input
            type="text"
            {...register('billingAddress', {
              required: 'Address line 1 is required',
            })}
            variant="filled"
            size="md"
            w="full"
          />
          <FormErrorMessage>
            {errors.billingAddress && errors.billingAddress.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl fontSize={{ base: 'sm', md: 'md' }} color="primary.100" isInvalid={errors.city}>
          <FormLabel>City</FormLabel>
          <Input
            type="text"
            {...register("city", { required: "City is required" })}
            variant="filled"
          />
          <FormErrorMessage>{errors.city && errors.city.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={errors.state}>
          <FormLabel>State</FormLabel>
          <Input
            type="text"
            {...register("state", { required: "State is required" })}
            variant="filled"
          />
          <FormErrorMessage>{errors.state && errors.state.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.country}>
          <FormLabel fontSize={{ base: "sm", md: "md" }} color="primary.100">
            Country
          </FormLabel>
          <Controller
            name="country"
            control={control}
            rules={{ required: "Country is required" }}
            fontSize={{ base: 'sm', md: 'md' }} color="primary.100"
            render={({ field }) => (
              <CustomSelect
                value={field.value}
                onChange={field.onChange}
                placeholder="Select a country"
                width="100%"
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </CustomSelect>
            )}
          />
          <FormErrorMessage>
            {errors.country?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={errors.postalCode}>
          <FormLabel fontSize={{ base: 'sm', md: 'md' }} color="primary.100">
            ZIP/Postal Code
          </FormLabel>
          <Input
            type="text"
            {...register('postalCode', {
              required: 'ZIP/Postal code is required',
            })}
            variant="filled"
            size="md"
            w="full"
          />
          <FormErrorMessage>
            {errors.postalCode && errors.postalCode.message}
          </FormErrorMessage>
        </FormControl>
      </SimpleGrid>
    </Box>
  );
}

const MerchantSettings = () => {
  const {
    userSubscription,
    paymentMethods,
    getPaymentMethods,
    setDefaultPaymentMethod,
    createPaymentMethod,
    addPaymentMethod,
    stopAutoRenewal,
    startAutoRenewal,
    deletePaymentMethod,
    loading,
    fetchUserSubscription
  } = useSubscription();

  const stripe = useStripe();
  const elements = useElements();
  const { register, handleSubmit, formState: { errors }, control } = useForm();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [isAdding, setIsAdding] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const toast = useToast();
  const { user } = useAuth();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState(null);

  const COUNTRIES = [
    { name: "United States", code: "US" },
    { name: "Canada", code: "CA" },
    { name: "United Kingdom", code: "GB" },
    { name: "Australia", code: "AU" },
    { name: "Germany", code: "DE" },
    { name: "France", code: "FR" },
    { name: "India", code: "IN" },
    { name: "Japan", code: "JP" },
    { name: "Brazil", code: "BR" },
    { name: "Mexico", code: "MX" },
    { name: "South Africa", code: "ZA" },
    { name: "Singapore", code: "SG" },
  ];

  const CARD_OPTIONS = {
    style: {
      base: {
        fontSize: "16px",
        color: "#424770",
        fontFamily: "Poppins, sans-serif",
        "::placeholder": { color: "#aab7c4" },
        padding: "12px",
        backgroundColor: "transparent",
        borderRadius: "6px",
      },
      invalid: {
        color: "#ff4c4c",
        iconColor: "#ff4c4c",
      },
      complete: {
        color: "#4caf50",
      },
    },
    hidePostalCode: true,
    disableLink: true,
  };

  useEffect(() => {
    getPaymentMethods();
  }, []);

  const onSubmit = async (data) => {
    setIsPaymentLoading(true);
    try {
      if (selectedPaymentMethod === 'card') {
        const cardElement = elements.getElement(CardElement);
        const billingDetails = {
          name: data.cardName,
          address: {
            line1: data.billingAddress,
            city: data.city,
            state: data.state,
            postal_code: data.postalCode,
            country: data.country,
          },
        };
        const paymentMethodId = await createPaymentMethod(cardElement, stripe, billingDetails);
        await addPaymentMethod(paymentMethodId);
        setIsAdding(false);
        toast({
          title: "Payment method added successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error adding payment method:', error);
      toast({
        title: "Error adding payment method",
        description: error.message || "Please try again",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      await setDefaultPaymentMethod(paymentMethodId);
      toast({
        title: "Default payment method updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to set default payment method",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClick = (paymentMethodId) => {
    setPaymentMethodToDelete(paymentMethodId);
    onOpen();
  };

  const handleConfirmDelete = async () => {
    try {
      await deletePaymentMethod(paymentMethodToDelete);
      toast({
        title: "Payment method removed",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete payment method",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleToggleAutoRenewal = async () => {
    try {
      // cancelation_at_period_end = true means auto-renew is OFF
      // cancelation_at_period_end = false means auto-renew is ON
      if (!userSubscription?.cancelation_at_period_end) {
        // Currently auto-renewing, so disable it
        await stopAutoRenewal();
        toast({
          title: "Auto-renewal disabled",
          description: "Your subscription will not renew automatically",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Currently NOT auto-renewing, so enable it
        await startAutoRenewal();
        toast({
          title: "Auto-renewal enabled",
          description: "Your subscription will renew automatically",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      await fetchUserSubscription();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update auto-renewal",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      {/* Subscription Section */}
      <Box mb={8}>
        <Heading size="md" mb={4} color="dark.900">Current Subscription</Heading>
        {loading ? (
          <Spinner />
        ) : userSubscription ? (
          <Card bg="secondary.900" borderColor="secondary.900">
            <CardBody>
              <VStack align="start" spacing={3}>
                <HStack justify="space-between" w="full">
                  <Text fontSize="lg" fontWeight="bold" color="primary.100">
                    {userSubscription.plan || 'Subscription Plan'}
                  </Text>
                  <Badge colorScheme={userSubscription.status === 'active' ? "green" : "red"}>
                    {userSubscription.status === 'active' ? "Active" : "Inactive"}
                  </Badge>
                </HStack>
                {userSubscription.unit_amount && (
                  <Text color="primary.500">
                    ${(userSubscription.unit_amount / 100).toFixed(2)} / month
                  </Text>
                )}
                {userSubscription.current_period_end && (
                  <Text color="primary.900" fontSize="sm">
                    {!userSubscription.cancelation_at_period_end ? 'Renews' : 'Expires'} on:{' '}
                    {new Date(userSubscription.current_period_end * 1000).toLocaleDateString()}
                  </Text>
                )}
                <Button
                  size="sm"
                  colorScheme={!userSubscription.cancelation_at_period_end ? "red" : "green"}
                  onClick={handleToggleAutoRenewal}
                >
                  {!userSubscription.cancelation_at_period_end ? "Disable" : "Enable"} Auto-Renewal
                </Button>
              </VStack>
            </CardBody>
          </Card>
        ) : (
          <Text color="primary.900">No active subscription</Text>
        )}
      </Box>

      <Divider my={6} />

      {/* Payment Methods Section */}
      <Box>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md" color="dark.900">Payment Methods</Heading>
          <Button leftIcon={<AddIcon />} onClick={() => setIsAdding(!isAdding)} colorScheme="teal">
            {isAdding ? 'Cancel' : 'Add Payment Method'}
          </Button>
        </Flex>

        {isAdding && (
          <Box mb={6} p={4} bg="secondary.100" borderRadius="md">
            <form onSubmit={handleSubmit(onSubmit)}>
              <PaymentMethodSelect
                selectedPaymentMethod={selectedPaymentMethod}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
              />
              {selectedPaymentMethod === 'card' && (
                <>
                  <BillingDetailsForm
                    register={register}
                    errors={errors}
                    countries={COUNTRIES}
                    control={control}
                  />
                  <FormControl mt={4}>
                    <FormLabel fontSize={{ base: 'sm', md: 'md' }} color="primary.500">Card Details</FormLabel>
                    <Box
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      bg="white"
                      _focus={{ borderColor: "primary.500", boxShadow: "0px 0px 8px rgba(72, 187, 120, 0.8)" }}
                    >
                      <CardElement options={CARD_OPTIONS} />
                    </Box>
                  </FormControl>
                  <Button type="submit" mt={4} w="full" colorScheme="green" isLoading={isPaymentLoading}>
                    Add Card
                  </Button>
                </>
              )}
            </form>
          </Box>
        )}

        {loading ? (
          <Spinner />
        ) : paymentMethods && paymentMethods.length > 0 ? (
          <VStack spacing={3} align="stretch">
            {paymentMethods.map((method) => (
              <Card key={method.id} bg="secondary.900">
                <CardBody>
                  <Flex justify="space-between" align="center">
                    <HStack>
                      <Icon as={FaCreditCard} boxSize={6} color="primary.500" />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="bold" color="primary.100">
                          {method.card?.brand?.toUpperCase()} ****{method.card?.last4}
                        </Text>
                        <Text fontSize="sm" color="primary.900">
                          Expires {method.card?.exp_month}/{method.card?.exp_year}
                        </Text>
                      </VStack>
                      {method.is_default && (
                        <Badge colorScheme="green" ml={2}>Default</Badge>
                      )}
                    </HStack>
                    <HStack>
                      {!method.is_default && (
                        <Button size="sm" onClick={() => handleSetDefault(method.id)}>
                          Set Default
                        </Button>
                      )}
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDeleteClick(method.id)}
                        aria-label="Delete payment method"
                      />
                    </HStack>
                  </Flex>
                </CardBody>
              </Card>
            ))}
          </VStack>
        ) : (
          <Text color="primary.900">No payment methods saved</Text>
        )}
      </Box>

      <ConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmDelete}
        title="Delete Payment Method"
        message="Are you sure you want to delete this payment method?"
      />
    </Box>
  );
};

export default MerchantSettings;
